import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import {
  Button,
  CheckboxInput,
  Footer,
  Panel,
  RadioInput,
  SelectInput,
} from 'melp-design/components';
import { Collapse, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRouteParams } from 'utils/useIdParam';
import { FC, useEffect } from 'react';
import { DiscountPackage, discountPackages } from 'store/discounts';
import {
  ClientFeature,
  clientFeatures,
  useAdminClient,
  useAdminUpdateClient,
} from 'store/admin-clients';
import { ROUTES } from 'config';
import { useSearchParams } from 'utils/navigation';
import { adminDepositEventsFiltersSchema } from 'store/admin-client-deposits';
import { usePredefinedToasts } from 'utils/Toast';
import {
  ClaimBenefitCombination,
  claimBenefitCombinations,
} from 'store/companies';
import { SUPPORTED_TAX_EXEMPTION_COUNTRIES } from 'store/shop';
import { formatCountry } from 'utils/format';

interface Values {
  features: Record<ClientFeature, boolean>;
  package: DiscountPackage;
  expenseCompensationBenefitCombination: ClaimBenefitCombination;
}

const INITIAL_VALUES: Values = {
  features: {
    benefits: false,
    news: false,
    recognition: false,
    marketplace: false,
    marketplaceV2: false,
    claimReimbursement: false,
    taxExemptionEST: false,
    companyItems: false,
    discounts: false,
    manualExpenses: false,
    comments: false,
    employeePayment: false,
    conversationalAgent: false,
    ai: false,
    trustbox: false,
    trainings: false,
    forms: false,
    colleagues: false,
    orgStructure: false,
    advancedPermissions: false,
    advancedPermissionsView: false,
  },
  package: 'basic',
  expenseCompensationBenefitCombination: 'any',
};

export const AdminClientFeatures: FC = () => {
  const { t, i18n } = useTranslation();
  const { id: clientId } = useRouteParams(
    ROUTES.admin.clients.details.features,
  );
  const { navigate } = useSearchParams(adminDepositEventsFiltersSchema);
  const predefinedToasts = usePredefinedToasts();

  const { data: client } = useAdminClient(clientId);

  const { mutate: updateClient, isLoading: isUpdating } =
    useAdminUpdateClient();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<Values>({ defaultValues: INITIAL_VALUES });

  useEffect(() => {
    if (client) {
      reset({
        features: clientFeatures.reduce(
          (acc, cur) => ({
            ...acc,
            [cur]: client.features.includes(cur),
          }),
          {},
        ),
        package: client.settings.discountPlan,
        expenseCompensationBenefitCombination:
          client.settings.expenseCompensationBenefitCombination,
      });
    }
  }, [client, reset]);

  const [features] = watch(['features']);

  const onSubmit: SubmitHandler<Values> = (values) => {
    updateClient(
      {
        clientId,
        data: {
          features: clientFeatures.filter(
            (feature) => !!values.features[feature],
          ),
          discountPlan: values.package,
          expenseCompensationBenefitCombination:
            values.expenseCompensationBenefitCombination,
        },
      },
      {
        onSuccess: () => {
          predefinedToasts.success.updated();
        },
      },
    );
  };

  if (!client) return null;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Panel title={t('sidebar.features')}>
        <Stack spacing={3}>
          <Controller
            name="features.benefits"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <CheckboxInput
                label={t('clients.feature-benefits')}
                name={name}
                checked={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />

          <Controller
            name="features.news"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <CheckboxInput
                label={t('clients.feature-news')}
                name={name}
                checked={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />

          <Controller
            name="features.recognition"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <CheckboxInput
                label={t('clients.feature-recognition')}
                name={name}
                checked={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />

          <Controller
            name="features.trustbox"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <CheckboxInput
                label={t('trustBox.trustBox')}
                name={name}
                checked={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />

          <Controller
            name="features.comments"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <CheckboxInput
                label={t('clients.feature-comments')}
                name={name}
                checked={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />

          <Stack gap={1.5}>
            <Controller
              name="features.marketplace"
              control={control}
              render={({ field: { name, value, onChange, ref } }) => (
                <CheckboxInput
                  label={t('clients.feature-marketplace')}
                  name={name}
                  checked={value}
                  onChange={onChange}
                  ref={ref}
                />
              )}
            />

            <Collapse in={features.marketplace}>
              <Stack gap={1} pl={3} alignItems="flex-start">
                <Button
                  label={t('deposits.actions.manage')}
                  size="sm"
                  variant="tertiary"
                  onClick={() => {
                    navigate({
                      url: ROUTES.admin.clients.details.depositEvents.list.replace(
                        ':id',
                        clientId,
                      ),
                      params: { type: ['shopOrder'] },
                    });
                  }}
                />
              </Stack>
            </Collapse>
          </Stack>

          <Stack gap={1.5}>
            <Controller
              name="features.marketplaceV2"
              control={control}
              render={({ field: { name, value, onChange, ref } }) => (
                <CheckboxInput
                  label={`${t('clients.feature-marketplace')} v2`}
                  name={name}
                  checked={value}
                  onChange={onChange}
                  ref={ref}
                />
              )}
            />

            <Collapse in={features.marketplaceV2}>
              <Stack gap={1} pl={3} alignItems="flex-start">
                <Button
                  label={t('deposits.actions.manage')}
                  size="sm"
                  variant="tertiary"
                  onClick={() => {
                    navigate({
                      url: ROUTES.admin.clients.details.depositEvents.list.replace(
                        ':id',
                        clientId,
                      ),
                      params: { type: ['shopOrder'] },
                    });
                  }}
                />
              </Stack>
            </Collapse>
          </Stack>

          <Stack gap={1.5}>
            <Controller
              name="features.companyItems"
              control={control}
              render={({ field: { name, value, onChange, ref } }) => (
                <CheckboxInput
                  label={t('clients.feature-companyItems')}
                  name={name}
                  checked={value}
                  onChange={onChange}
                  ref={ref}
                />
              )}
            />

            <Collapse in={features.companyItems}>
              <Stack gap={1} pl={3} alignItems="flex-start">
                <Button
                  label={t('deposits.actions.manage')}
                  size="sm"
                  variant="tertiary"
                  onClick={() => {
                    navigate({
                      url: ROUTES.admin.clients.details.depositEvents.list.replace(
                        ':id',
                        clientId,
                      ),
                      params: { type: ['companyItem'] },
                    });
                  }}
                />
              </Stack>
            </Collapse>
          </Stack>

          <Controller
            name="features.employeePayment"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <CheckboxInput
                label={t('clients.feature-employeePayment')}
                name={name}
                checked={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />

          <Stack gap={1.5}>
            <Controller
              name="features.claimReimbursement"
              control={control}
              render={({ field: { name, value, onChange, ref } }) => (
                <CheckboxInput
                  label={t('clients.feature-claimReimbursement')}
                  name={name}
                  checked={value}
                  onChange={onChange}
                  ref={ref}
                />
              )}
            />

            <Collapse in={features.claimReimbursement}>
              <Stack gap={2} pl={3} alignItems="flex-start">
                <Button
                  label={t('deposits.actions.manage')}
                  size="sm"
                  variant="tertiary"
                  onClick={() => {
                    navigate({
                      url: ROUTES.admin.clients.details.depositEvents.list.replace(
                        ':id',
                        clientId,
                      ),
                      params: { type: ['claimReimbursement'] },
                    });
                  }}
                />

                <Controller
                  name="expenseCompensationBenefitCombination"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <RadioInput
                        label={t(
                          'claims.settings.claimBenefitCombination.label',
                        )}
                        name={name}
                        value={value}
                        onChange={onChange}
                        items={claimBenefitCombinations.map((method) => ({
                          label: t(`claims.benefit_combination.${method}`),
                          value: method,
                        }))}
                        ref={ref}
                      />
                    );
                  }}
                />

                {SUPPORTED_TAX_EXEMPTION_COUNTRIES.map((country) => (
                  <Controller
                    name={`features.taxExemption${country}`}
                    control={control}
                    key={country}
                    render={({ field: { name, value, onChange, ref } }) => (
                      <CheckboxInput
                        label={t(
                          'clients.feature-taxExemptExpenseCompensation',
                          { country: formatCountry(i18n.language, country) },
                        )}
                        name={name}
                        checked={value}
                        onChange={onChange}
                        ref={ref}
                      />
                    )}
                  />
                ))}
              </Stack>
            </Collapse>
          </Stack>

          <Stack gap={1.5}>
            <Controller
              name="features.manualExpenses"
              control={control}
              render={({ field: { name, value, onChange, ref } }) => (
                <CheckboxInput
                  label={t('expenses.filters.type.options.manual')}
                  name={name}
                  checked={value}
                  onChange={onChange}
                  ref={ref}
                />
              )}
            />

            <Collapse in={features.manualExpenses}>
              <Stack gap={1} pl={3} alignItems="flex-start">
                <Button
                  label={t('deposits.actions.manage')}
                  size="sm"
                  variant="tertiary"
                  onClick={() => {
                    navigate({
                      url: ROUTES.admin.clients.details.depositEvents.list.replace(
                        ':id',
                        clientId,
                      ),
                      params: { type: ['manual'] },
                    });
                  }}
                />
              </Stack>
            </Collapse>
          </Stack>

          <Stack gap={1.5}>
            <Controller
              name="features.discounts"
              control={control}
              render={({ field: { name, value, onChange, ref } }) => (
                <CheckboxInput
                  label={t('clients.feature-discounts')}
                  name={name}
                  checked={value}
                  onChange={onChange}
                  ref={ref}
                />
              )}
            />

            <Collapse in={features.discounts}>
              <Stack gap={1} pl={3} alignItems="flex-start">
                <Controller
                  name="package"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <SelectInput
                      label={t('discounts.package.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      options={discountPackages
                        .filter((pack) => pack !== 'internal')
                        .map((pack) => ({
                          label: t(`discounts.package.${pack}`),
                          value: pack,
                        }))}
                      required
                      ref={ref}
                    />
                  )}
                />
              </Stack>
            </Collapse>
          </Stack>

          <Controller
            name="features.ai"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <CheckboxInput
                label={t('clients.feature-ai')}
                name={name}
                checked={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />

          <Controller
            name="features.conversationalAgent"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <CheckboxInput
                label={t('clients.feature-conversationalAgent')}
                name={name}
                checked={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />

          <Controller
            name="features.trainings"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <CheckboxInput
                label={t('surveys.type.training.title')}
                name={name}
                checked={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />

          <Controller
            name="features.forms"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <CheckboxInput
                label={t('surveys.type.form.title')}
                name={name}
                checked={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />

          <Controller
            name="features.colleagues"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <CheckboxInput
                label={t('clients.feature-colleagues')}
                name={name}
                checked={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />

          <Controller
            name="features.orgStructure"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <CheckboxInput
                label={t('org.org_structure.title')}
                name={name}
                checked={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />

          <Controller
            name="features.advancedPermissionsView"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <CheckboxInput
                label={t('clients.feature-advancedPermissionsView')}
                name={name}
                checked={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />

          <Controller
            name="features.advancedPermissions"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <CheckboxInput
                label={t('clients.feature-advancedPermissions')}
                name={name}
                checked={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />
        </Stack>
      </Panel>

      <Footer visible={isDirty}>
        <Stack direction="row" justifyContent="end" gap="10px">
          <Button
            label={t('common.cancel')}
            variant="neutral-outline"
            onClick={() => {
              reset(undefined, { keepDirty: false });
            }}
          />
          <Button
            label={t('common.save')}
            variant="primary"
            type="submit"
            disabled={isUpdating}
          />
        </Stack>
      </Footer>
    </form>
  );
};
